import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "components/basic/button";
import InlineEditRow from "components/basic/inlineEditRow";
import { formatPhoneNumber } from "pages/formTools";
import Typography from "components/basic/typography";
import { useNavigate } from "react-router-dom";
import SignUpReviewLayout from "../../components/SignUpReviewLayout";
import { useSnack } from "hooks/useSnack";
import { useCallback, useState } from "react";
import { useRegisterAccountMutation } from "generated/graphql";
import { Account } from "../..";
import { clearSignUpDraft } from "../../helpers/signUpDraft";

// ─── Section header ────────────────────────────────────────────────────────────
const SectionHeader: React.FC<{ title: string }> = ({ title }) => {
  const theme = useTheme();
  return (
    <Typography
      variant="h5"
      style={{
        fontWeight: "bold",
        marginTop: theme.spacing(2.5),
        marginBottom: theme.spacing(0.5),
        borderBottom: `2px solid ${theme.palette.warning.main}`,
        paddingBottom: theme.spacing(0.5),
      }}
    >
      {title}
    </Typography>
  );
};

interface UserReviewFormProps {
  signUpData: Account;
  setSignUpData: React.Dispatch<React.SetStateAction<Account>>;
}

const UserReviewForm: React.FC<UserReviewFormProps> = ({
  signUpData,
  setSignUpData,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const showSnack = useSnack();
  const { t } = useTranslation("auth");
  const [, registerAccount] = useRegisterAccountMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const set = (field: keyof Account) => (val: string) =>
    setSignUpData((prev) => ({ ...prev, [field]: val }));

  const handleCreateAccount = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const response = await registerAccount(
        {
          options: {
            email: signUpData.email,
            password: signUpData.password,
            firstName: signUpData.firstName,
            lastName: signUpData.lastName,
            userType: signUpData.userType,
            googleAccessToken: signUpData.googleAccessToken || undefined,
            profilePhotoUrl: signUpData.profilePhotoUrl || undefined,
            phoneNumber: signUpData.phoneNumber,
            address: signUpData.address,
            latitude: signUpData.latitude ?? undefined,
            longitude: signUpData.longitude ?? undefined,
            placeId: signUpData.placeId ?? undefined,
            onboardingCompleted: true,
          },
        },
        { requestPolicy: "network-only" },
      );

      if (response.data?.registerAccount.errors) {
        const errorMsg = response.data.registerAccount.errors
          .map((e) => e.message)
          .join(", ");
        showSnack(errorMsg || t("common.failed_try_later"), "error");
        return;
      }

      clearSignUpDraft();
      showSnack(t("common.registered"), "success");
      navigate("/");
    } catch (error) {
      console.error("Registration error:", error);
      showSnack(t("common.unexpected"), "error");
    } finally {
      setIsSubmitting(false);
    }
  }, [signUpData, registerAccount, navigate, showSnack, t]);

  return (
    <SignUpReviewLayout maxWidth={560}>
      <Typography
        variant="h4"
        align="center"
        style={{ fontWeight: "bold", marginBottom: theme.spacing(3) }}
      >
        {t("review.title")}
      </Typography>

      <SectionHeader title={t("review.account")} />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: `${theme.spacing(0.75)} 0`,
        }}
      >
        <Typography variant="body2" style={{ opacity: 0.7 }}>
          {t("review.email")}
        </Typography>
        <Typography
          variant="body2"
          style={{
            fontWeight: "bold",
            textAlign: "right",
            overflowWrap: "anywhere",
          }}
        >
          {signUpData.email}
        </Typography>
      </div>

      <SectionHeader title={t("review.name")} />
      <InlineEditRow
        label={t("review.first_name")}
        value={signUpData.firstName}
        onSave={set("firstName")}
      />
      <InlineEditRow
        label={t("review.last_name")}
        value={signUpData.lastName}
        onSave={set("lastName")}
      />

      <SectionHeader title={t("review.contact_details")} />
      <InlineEditRow
        label={t("review.phone")}
        value={signUpData.phoneNumber}
        onSave={set("phoneNumber")}
        mask={formatPhoneNumber}
      />
      <InlineEditRow
        label={t("review.address")}
        value={signUpData.address}
        onSave={set("address")}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: theme.spacing(2),
          paddingTop: theme.spacing(3),
          paddingBottom: theme.spacing(4),
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="large"
          style={{
            color: theme.palette.common.white,
            fontWeight: "bold",
            padding: `0 ${theme.spacing(3.5)}`,
          }}
          onClick={() =>
            navigate(
              signUpData.authProvider === "google"
                ? "?currentPage=addressForm"
                : "?currentPage=emailVerificationForm",
            )
          }
          disabled={isSubmitting}
        >
          {t("common.back")}
        </Button>

        <Button
          variant="contained"
          color="warning"
          size="large"
          style={{
            color: theme.palette.common.white,
            fontWeight: "bold",
            padding: `0 ${theme.spacing(3.5)}`,
          }}
          onClick={handleCreateAccount}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {t("common.create_account")}
        </Button>
      </div>
    </SignUpReviewLayout>
  );
};

export default UserReviewForm;
